import React from 'react';
import SVGIcon from '../SVGIcon/SVGIcon';

import {
  StyledButton,
  StyledButtonIconSpan,
  StyledButtonTextSpan,
  StyledIconButton
} from './Button.style';

type ButtonAttributes = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'color'>;

interface ButtonProps extends ButtonAttributes {
  contentType?: 'text' | 'icon' | 'textWithIcon' | 'toggle';
  color?: 'default' | 'primary';
  variant?: 'text' | 'outlined' | 'filled';
  size?: 'small' | 'medium' | 'large';
  icon?: string | false;
  iconPosition?: 'left' | 'right' | 'none';
  disabled?: boolean;
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className?: string;
  dataAttributes?: Record<string, unknown>;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | false;
}

/**
 * A button makes it possible for users to fulfill actions in the user interface.
 */

const Button = ({
  disabled = false,
  color = 'default',
  variant = 'text',
  className = '',
  contentType = 'text',
  icon = false,
  iconPosition = 'none',
  size = 'small',
  dataAttributes = {},
  onClick = false,
  children = null,
  ...buttonAttributes
}: ButtonProps) => {
  const onClickProperty: { onClick?: React.MouseEventHandler<HTMLButtonElement> } = {};
  if (onClick) onClickProperty.onClick = onClick;

  /* Test data attributes */
  const testDataAttributes = dataAttributes;
  testDataAttributes['data-test-disabled'] = disabled;
  testDataAttributes['data-test-color'] = color;
  testDataAttributes['data-test-content-type'] = contentType;

  /* For the button contentType `toggle` guarantee the button size is set to small */
  size = contentType === 'toggle' ? 'small' : size;

  const renderIcon = () => {
    // For the button of contentType 'icon', let the size of the icon be defined by the icon button style.
    let iconSize = contentType === 'icon' ? 'inherit' : size;
    /* For the button contentType `toggle` guarantee the icon size is set to medium */
    iconSize = contentType === 'toggle' ? 'large' : iconSize;

    return (
      <StyledButtonIconSpan
        data-test-container="icon"
        data-test-variant={variant}
        $position={contentType === 'icon' ? 'none' : iconPosition}
        $size={size}
        $contentType={contentType}
      >
        <SVGIcon icon={icon} size={iconSize} />
      </StyledButtonIconSpan>
    );
  };

  const renderTextButton = (withIcon: boolean) => (
    <StyledButton
      disabled={disabled}
      $color={color}
      $size={size}
      $contentType={contentType}
      $variant={variant}
      className={className}
      {...onClickProperty}
      {...testDataAttributes}
      {...buttonAttributes}
    >
      {withIcon && iconPosition === 'left' && renderIcon()}
      <StyledButtonTextSpan>{children}</StyledButtonTextSpan>
      {withIcon && iconPosition === 'right' && renderIcon()}
    </StyledButton>
  );

  const renderIconButton = () => (
    <StyledIconButton
      $color={color}
      disabled={disabled}
      $variant={variant}
      $size={size}
      className={className}
      {...onClickProperty}
      {...testDataAttributes}
      {...buttonAttributes}
    >
      {renderIcon()}
    </StyledIconButton>
  );

  if (contentType !== 'icon')
    return renderTextButton(contentType === 'textWithIcon' || contentType === 'toggle');
  return renderIconButton();
};

export default Button;
